import React, { useCallback, useEffect, useState } from "react";
import SideBar from "../../components/SideBar/SideBar";
import TopNav from "../../components/TopNav/TopNav";
import api from "../../services/api";
import { format } from "date-fns";

// Every text sent to a ZONA line. Two sources feed this: the Twilio webhook
// (messages we received and routed) and a 5-minute poller against Twilio's API,
// which is the only way blocked messages ever show up -- Twilio filters OTP
// bodies before delivery, so those never reach the webhook at all.
const LINE_LABELS = {
  "+18669023933": { label: "866 · Live line", color: "#0A3FD6" },
  "+17865654056": { label: "786 · Concierge Demo", color: "#4F30EA" },
};

const OUTCOME_LABELS = {
  routed: { label: "Routed", color: "#16A34A" },
  duplicate: { label: "Duplicate", color: "#6B7280" },
  dropped_unknown_sender: { label: "Dropped · unknown sender", color: "#B45309" },
  blocked_by_twilio_otp_filter: { label: "Blocked · Twilio OTP filter", color: "#B91C1C" },
  webhook_missed: { label: "Delivered · we missed it", color: "#B45309" },
  never_delivered: { label: "Never delivered", color: "#B91C1C" },
  access_unlocked: { label: "Unlocked with access code", color: "#16A34A" },
  access_denied: { label: "Locked · no valid code", color: "#B45309" },
  access_code_not_set: { label: "Locked · no code set", color: "#B45309" },
  access_lockout_tripped: { label: "Lockout tripped", color: "#B91C1C" },
  access_locked_out: { label: "Blocked · locked out", color: "#B91C1C" },
  access_already_unlocked: { label: "Code resent", color: "#6B7280" },
  access_locked_by_owner: { label: "Locked by owner", color: "#6B7280" },
};

const InboundTexts = () => {
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [line, setLine] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const records = 50;

  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/admin/inbound-sms`, {
        params: { page, records, ...(line ? { to: line } : {}), ...(q ? { q } : {}) },
      });
      setMessages(data?.data || []);
      setTotal(data?.total || 0);
    } catch (err) {
      console.error("Failed to fetch inbound texts:", err);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [page, line, q]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  useEffect(() => {
    api.get(`/admin/inbound-sms/stats`)
      .then(({ data }) => setStats(data?.data || null))
      .catch((err) => console.error("Failed to fetch inbound text stats:", err));
  }, []);

  const formatDate = (d) => {
    if (!d) return "—";
    try {
      return format(new Date(d), "MMM d, yyyy h:mm a");
    } catch {
      return "—";
    }
  };

  const renderLine = (to) => {
    const meta = LINE_LABELS[to] || { label: to || "—", color: "#6B7280" };
    return (
      <span style={{ background: meta.color, color: "#fff", fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 999, whiteSpace: "nowrap" }}>
        {meta.label}
      </span>
    );
  };

  const renderOutcome = (m) => {
    const meta = OUTCOME_LABELS[m.outcome];
    if (!meta) return <span style={{ opacity: 0.4 }}>—</span>;
    return (
      <span style={{ color: meta.color, fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>
        {meta.label}
      </span>
    );
  };

  // A blocked message's body is redacted by Twilio before it is stored, so
  // showing the stored text would read as a half-written message rather than
  // the truth, which is that the content never reached us.
  const renderBody = (m) => {
    if (m.blocked) {
      return (
        <span style={{ opacity: 0.65, fontStyle: "italic" }}>
          Content stripped by Twilio before delivery (error 30038)
        </span>
      );
    }
    if (!m.body) {
      return <span style={{ opacity: 0.4 }}>{m.num_media > 0 ? `${m.num_media} attachment(s)` : "—"}</span>;
    }
    return m.body;
  };

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content" style={{ padding: 24 }}>
          <div style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
            <div>
              <h1 style={{ margin: 0, color: "#fff" }}>Inbound Texts</h1>
              <p style={{ margin: "4px 0 0", color: "rgba(255,255,255,0.5)", fontSize: 13 }}>
                Every text sent to a ZONA line, including the ones Twilio blocked before they reached us.
              </p>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="text"
                placeholder="Search message text…"
                value={q}
                onChange={(e) => { setPage(1); setQ(e.target.value); }}
                style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", padding: "8px 12px", borderRadius: 8, fontSize: 13, outline: "none", width: 240 }}
              />
              <select
                value={line}
                onChange={(e) => { setPage(1); setLine(e.target.value); }}
                style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", padding: "8px 12px", borderRadius: 8, fontSize: 13, outline: "none" }}
              >
                <option value="">All lines</option>
                <option value="+18669023933">866 · Live line</option>
                <option value="+17865654056">786 · Concierge Demo</option>
              </select>
            </div>
          </div>

          {stats && (
            <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
              <StatTile label="Texts in last 24h" value={stats.total} />
              <StatTile label="Blocked by Twilio" value={stats.blocked} accent={stats.blocked > 0 ? "#B91C1C" : null} />
              <StatTile label="Unknown senders" value={stats.dropped} accent={stats.dropped > 0 ? "#B45309" : null} />
            </div>
          )}

          <div style={{ width: "100%", background: "rgba(255,255,255,0.03)", borderRadius: 12, border: "1px solid rgba(255,255,255,0.06)", overflowX: "auto", overflowY: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", color: "#fff", minWidth: 900 }}>
              <thead>
                <tr style={{ background: "rgba(255,255,255,0.04)" }}>
                  <th style={th}>Line</th>
                  <th style={th}>From</th>
                  <th style={th}>Received</th>
                  <th style={th}>Message</th>
                  <th style={th}>Outcome</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} style={{ ...td, textAlign: "center", padding: 32, opacity: 0.6 }}>Loading…</td></tr>
                ) : messages.length === 0 ? (
                  <tr><td colSpan={5} style={{ ...td, textAlign: "center", padding: 32, opacity: 0.6 }}>No texts yet.</td></tr>
                ) : (
                  messages.map((m) => (
                    <tr key={m.message_sid} style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                      <td style={td}>{renderLine(m.to)}</td>
                      <td style={td}>{m.from || <span style={{ opacity: 0.4 }}>—</span>}</td>
                      <td style={{ ...td, whiteSpace: "nowrap" }}>{formatDate(m.sent_at)}</td>
                      <td style={{ ...td, maxWidth: 420, wordBreak: "break-word" }}>{renderBody(m)}</td>
                      <td style={td}>{renderOutcome(m)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {total > records && (
            <div style={{ display: "flex", justifyContent: "center", gap: 12, marginTop: 16, color: "#fff" }}>
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} style={pageBtn(page <= 1)}>Prev</button>
              <span style={{ fontSize: 13, alignSelf: "center", opacity: 0.7 }}>
                Page {page} of {Math.ceil(total / records)}
              </span>
              <button type="button" disabled={page >= Math.ceil(total / records)} onClick={() => setPage((p) => p + 1)} style={pageBtn(page >= Math.ceil(total / records))}>Next</button>
            </div>
          )}
        </article>
      </div>
    </main>
  );
};

const StatTile = ({ label, value, accent }) => (
  <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: "12px 18px", minWidth: 150 }}>
    <div style={{ fontSize: 11, letterSpacing: 1.2, textTransform: "uppercase", color: "rgba(255,255,255,0.5)", fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 700, color: accent || "#fff", marginTop: 4 }}>{value}</div>
  </div>
);

const th = { textAlign: "left", padding: "12px 14px", fontSize: 11, fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase", color: "rgba(255,255,255,0.6)" };
const td = { padding: "12px 14px", fontSize: 13, verticalAlign: "middle" };
const pageBtn = (disabled) => ({
  background: disabled ? "transparent" : "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.12)",
  color: disabled ? "rgba(255,255,255,0.3)" : "#fff",
  padding: "6px 14px",
  borderRadius: 8,
  fontSize: 13,
  cursor: disabled ? "default" : "pointer",
});

export default InboundTexts;
